<template>
  <div>
    <div class="table-wrapper">
      <div class="scrollable">
        <AppTable
          :loading="showTableLoading"
          :table-data="expenses"
        >
          <template #empty>
            <ExpensesNoData />
          </template>

          <AppTableColumn
            v-slot="{ row }"
            header="Member"
          >
            <MemberPaymentAccountPopup
              :organization-id="organization.id"
              :user-id="row.user.id"
              :image-url="row.user.avatar_url.speck"
              :name="row.user.name"
              :show-member-profile="canShowMemberProfile"
              tab="info"
              title=""
            />
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header="Date"
          >
            {{ $filters.formatDate(row.receipt_date) }}
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header="Description"
          >
            <span>
              {{ row.description }}
              <a
                v-if="row.receipt.original_url"
                :href="row.receipt.original_url"
                class="download-receipt"
              >
                <i class="hi hi-16 hi-clip" />
              </a>
            </span>
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header="Amount"
          >
            {{ $filters.currency(row.amount, organization.currency) }}
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header="Category"
          >
            <small
              class="label"
              :style="{ border: '1px solid', color: categoryColor(row.category) }"
            >
              {{ row.category }}
            </small>
          </AppTableColumn>
          <AppTableColumn
            v-if="showProjects"
            v-slot="{ row }"
            :header="projectColumnLabel"
          >
            <a
              v-tooltip
              :href="`/projects/${row.project.id}`"
              target="_blank"
              class="project-name hover-underline d-flex align-items-center"
              title="View project details"
            >
              <AppAvatarInitial
                v-if="row.project.name"
                :text="row.project.name"
              />
              <div>{{ row.project.name }}</div>
            </a>
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header="Status"
          >
            <small
              v-if="row.billable && showInvoicesData"
              class="label"
              :class="statusClass(row.status.sym)"
            >
              {{ row.status.text }}
            </small>
            <small
              v-else-if="row.billable"
              class="label label-primary"
            >
              Billable
            </small>
            <small
              v-else
              class="label not-billable-label"
            >
              Not billable
            </small>
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header=""
          >
            <template v-if="row.editable">
              <div
                v-if="row.immutable"
                class="expense-locked step size-12"
              >
                <i
                  v-popover="{ html: true, placement: 'auto left' }"
                  class="hi-lock"
                  data-tipclass="expense-locked-popover"
                  :data-content="lockedExpensePopoverContent"
                />
              </div>
              <div
                v-else
                class="dropdown table-actions-dropdown expenses-action-dropdown"
              >
                <a
                  href="#"
                  data-toggle="dropdown"
                  role="button"
                  class="btn btn-sm btn-default dropdown-toggle"
                >
                  Actions
                  <i class="hi hi-caret hi-10" />
                </a>
                <ul class="dropdown-menu dropdown-menu-right">
                  <li>
                    <a
                      class="action"
                      @click="openEditDialog(row)"
                    >
                      Edit expense
                    </a>
                  </li>
                  <li>
                    <a
                      class="action"
                      @click="destroyExpense(row)"
                    >
                      Remove expense
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </AppTableColumn>
        </AppTable>
      </div>
    </div>
    <AppPagination
      v-if="showPagination"
      :pagination="pagination"
      :resource="{ one: 'expense', many: 'expenses' }"
      @page-changed="onPageChanged"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { stringToColor } from '~/utils/color_utils';
import { getProjectFlavourLabel } from '~/utils/project_flavour';
import MemberPaymentAccountPopup from '~/components/financials/MemberPaymentAccountPopup.vue';

export default {
  name: 'ExpensesTable',
  components: { MemberPaymentAccountPopup },
  props: {
    showInvoicesData: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['expenses', 'pagination', 'showTableLoading', 'canShowMemberProfile']),
    ...mapState(['organization']),
    hasData() {
      return this.expenses && this.expenses.length > 0;
    },
    showPagination() {
      return this.pagination && this.hasData;
    },
    lockedExpensePopoverContent() {
      const title = "<p class='expense-popover-title'>Expense is locked</p>";
      const reason =
        "<p class='step size-12'><i class='hi-check-circle-filled'></i><span class='expense-popover-reason'>Invoiced</span></p>";

      return title + reason;
    },
    projectColumnLabel() {
      return getProjectFlavourLabel(this.organization.project_flavour);
    },
    showProjects() {
      return this.organization.project_flavour !== 'work_orders';
    },
  },
  methods: {
    openEditDialog(expense) {
      this.$emit('open-edit-dialog', expense);
    },
    categoryColor(text) {
      return stringToColor(text);
    },
    statusClass(statusSym) {
      switch (statusSym) {
        case 'invoiced':
          return 'label-primary';
        case 'paid':
          return 'label-success';
        case 'un_invoiced':
          return 'label-danger';
      }
    },
    destroyExpense(expense) {
      if (confirm('Are you sure you want to delete this expense?')) {
        this.$store.dispatch('destroyExpense', { id: expense.id }).then(this.doneRequest, this.failedRequest);
      }
    },
    doneRequest(response) {
      if (response.success) {
        this.showMessage('Expense removed');
      } else {
        if (response.alert) {
          this.showMessage(response.alert);
        } else {
          this.showMessage('Unexpected error. Please try again.');
        }
      }
    },
    failedRequest(response) {
      this.showMessage('Unexpected error. Please try again.');
    },
    showMessage(message) {
      this.$appMessage(message);
    },
    onPageChanged(page) {
      this.$emit('page-changed', page);
    },
  },
};
</script>
